import {
    AlertOutlined,
    CloudUploadOutlined,
    ExclamationCircleOutlined,
    FileTextOutlined,
    MailOutlined,
    MessageOutlined,
    RobotOutlined,
    TeamOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { type RouterOutputs, trpc } from '@client/trpc/client';
import { formatFileSize } from '@shared/utils/core/file';
import { createFileRoute } from '@tanstack/react-router';
import { Alert, Card, Col, Divider, Empty, Row, Spin, Statistic, Typography } from 'antd';
import { BarChart3, Building2, Cpu, Server, TrendingDown, Users } from 'lucide-react';

type SystemStats = RouterOutputs['admin']['stats']['getSystemStats'];

export const Route = createFileRoute('/admin-console/monitoring/statistics')({
    staticData: {
        permissions: ['admin_orgs:read', 'admin_users:read', 'admin_system:read', 'admin_ai:read'],
        breadcrumb: {
            title: 'Platform statistics',
        },
        ui: {
            icon: BarChart3,
            title: 'Platform statistics',
            subtitle: 'Real-time metrics across the platform',
            widthVariant: 'default',
            menuDescription: 'View platform statistics and metrics',
        },
    },
    component: StatisticsPage,
});

// ============================================================================
// Section Header Component
// ============================================================================

function SectionHeader({ icon: Icon, title, color }: { icon: typeof Users; title: string; color: string }) {
    return (
        <div className="mb-4 flex items-center gap-2">
            <div
                className="flex size-8 items-center justify-center rounded-lg"
                style={{ backgroundColor: `${color}15`, color }}
            >
                <Icon size={18} />
            </div>
            <Typography.Title level={5} className="!mb-0">
                {title}
            </Typography.Title>
        </div>
    );
}

// ============================================================================
// Stat Card Component
// ============================================================================

function StatCard({
    title,
    icon,
    iconColor,
    stats,
    isLoading,
}: {
    title: string;
    icon: React.ReactNode;
    iconColor: string;
    stats: { label: string; value: string | number }[];
    isLoading: boolean;
}) {
    return (
        <Card className="h-full" styles={{ body: { padding: '16px' } }}>
            <div className="mb-3 flex items-center gap-2">
                <span style={{ color: iconColor }}>{icon}</span>
                <Typography.Text strong className="text-sm">
                    {title}
                </Typography.Text>
            </div>
            {isLoading ? (
                <div className="flex justify-center py-6">
                    <Spin />
                </div>
            ) : (
                <div className="space-y-3">
                    {stats.map((stat, index) => (
                        <div key={index} className="flex items-center justify-between">
                            <Typography.Text type="secondary" className="text-xs">
                                {stat.label}
                            </Typography.Text>
                            <Typography.Text strong>{stat.value}</Typography.Text>
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
}

// ============================================================================
// Summary Cards
// ============================================================================

function SummarySection({
    systemStats,
    isLoading,
    hasUsersAccess,
    hasOrgsAccess,
}: {
    systemStats: SystemStats | undefined;
    isLoading: boolean;
    hasUsersAccess: boolean;
    hasOrgsAccess: boolean;
}) {
    if (!hasUsersAccess && !hasOrgsAccess) return null;

    return (
        <Row gutter={[16, 16]} className="mb-6">
            {hasUsersAccess && (
                <Col xs={24} sm={12} md={6}>
                    <Card styles={{ body: { padding: '20px' } }}>
                        <Statistic
                            title="Total Users"
                            value={systemStats?.users.totalActiveUsers || 0}
                            loading={isLoading}
                            prefix={<UserOutlined className="text-green-500" />}
                        />
                    </Card>
                </Col>
            )}
            {hasOrgsAccess && (
                <Col xs={24} sm={12} md={6}>
                    <Card styles={{ body: { padding: '20px' } }}>
                        <Statistic
                            title="Total Organisations"
                            value={systemStats?.orgs.totalActiveOrgs || 0}
                            loading={isLoading}
                            prefix={<TeamOutlined className="text-purple-500" />}
                        />
                    </Card>
                </Col>
            )}
            {hasUsersAccess && (
                <Col xs={24} sm={12} md={6}>
                    <Card styles={{ body: { padding: '20px' } }}>
                        <Statistic
                            title="Active Today"
                            value={systemStats?.users.userLoggedInLast24Hours || 0}
                            loading={isLoading}
                            prefix={<UserOutlined className="text-blue-500" />}
                        />
                    </Card>
                </Col>
            )}
            {hasUsersAccess && (
                <Col xs={24} sm={12} md={6}>
                    <Card styles={{ body: { padding: '20px' } }}>
                        <Statistic
                            title="Active This Week"
                            value={systemStats?.users.userLoggedInLast7Days || 0}
                            loading={isLoading}
                            prefix={<UserOutlined className="text-cyan-500" />}
                        />
                    </Card>
                </Col>
            )}
        </Row>
    );
}

// ============================================================================
// Engagement Alert Banner
// ============================================================================

function EngagementAlertBanner() {
    const { hasAccess: hasOrgsAccess } = useAdminModulePermissions('orgs');

    const { data: engagementLeaderboard } = trpc.admin.stats.getEngagementLeaderboard.useQuery(
        {
            limit: 10,
            where: { tier: 'low' },
        },
        { enabled: hasOrgsAccess },
    );

    if (!hasOrgsAccess) return null;

    const atRiskCount = engagementLeaderboard?.rows?.length || 0;

    if (atRiskCount === 0) return null;

    return (
        <Alert
            type="warning"
            showIcon
            icon={<TrendingDown size={20} />}
            className="mb-6"
            title={
                <div className="flex items-center justify-between">
                    <div>
                        <Typography.Text strong>
                            {atRiskCount} Organisation{atRiskCount !== 1 ? 's' : ''} Need Attention
                        </Typography.Text>
                        <Typography.Text type="secondary" className="ml-2">
                            Low engagement detected.
                        </Typography.Text>
                    </div>
                    <Button.Link variant="secondary" to="/admin-console/monitoring/engagement">
                        View Engagement Dashboard
                    </Button.Link>
                </div>
            }
        />
    );
}

// ============================================================================
// Users & Organisations Section
// ============================================================================

function UsersOrgsSection({ systemStats, isLoading }: { systemStats: SystemStats | undefined; isLoading: boolean }) {
    const { hasAccess: hasUsersAccess } = useAdminModulePermissions('users');
    const { hasAccess: hasOrgsAccess } = useAdminModulePermissions('orgs');

    if (!hasUsersAccess && !hasOrgsAccess) return null;

    return (
        <div className="mb-8">
            <SectionHeader icon={Users} title="Users & Organisations" color="#8b5cf6" />
            <Row gutter={[16, 16]}>
                {hasUsersAccess && (
                    <Col xs={24} sm={12} lg={8}>
                        <StatCard
                            title="User Activity"
                            icon={<UserOutlined />}
                            iconColor="#22c55e"
                            isLoading={isLoading}
                            stats={[
                                { label: 'Total Users', value: systemStats?.users.totalActiveUsers || 0 },
                                { label: 'Last 24 Hours', value: systemStats?.users.userLoggedInLast24Hours || 0 },
                                { label: 'Last 7 Days', value: systemStats?.users.userLoggedInLast7Days || 0 },
                                { label: 'Last 30 Days', value: systemStats?.users.userLoggedInLast30Days || 0 },
                            ]}
                        />
                    </Col>
                )}
                {hasOrgsAccess && (
                    <Col xs={24} sm={12} lg={8}>
                        <StatCard
                            title="Organisations"
                            icon={<TeamOutlined />}
                            iconColor="#8b5cf6"
                            isLoading={isLoading}
                            stats={[{ label: 'Total Organisations', value: systemStats?.orgs.totalActiveOrgs || 0 }]}
                        />
                    </Col>
                )}
            </Row>
        </div>
    );
}

// ============================================================================
// Business Metrics Section
// ============================================================================

function BusinessMetricsSection({
    systemStats,
    isLoading,
}: {
    systemStats: SystemStats | undefined;
    isLoading: boolean;
}) {
    const { hasAccess } = useAdminModulePermissions('system');

    if (!hasAccess) return null;

    return (
        <div className="mb-8">
            <SectionHeader icon={Building2} title="Business Metrics" color="#f59e0b" />
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={8}>
                    <StatCard
                        title="Complaints"
                        icon={<ExclamationCircleOutlined />}
                        iconColor="#ef4444"
                        isLoading={isLoading}
                        stats={[
                            { label: 'Total', value: systemStats?.complaints.totalComplaints || 0 },
                            { label: 'Last 24 Hours', value: systemStats?.complaints.complaintsLast24Hours || 0 },
                            { label: 'Last 7 Days', value: systemStats?.complaints.complaintsLast7Days || 0 },
                            { label: 'Last 30 Days', value: systemStats?.complaints.complaintsLast30Days || 0 },
                        ]}
                    />
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <StatCard
                        title="Incidents"
                        icon={<AlertOutlined />}
                        iconColor="#f97316"
                        isLoading={isLoading}
                        stats={[
                            { label: 'Total', value: systemStats?.incidents.totalIncidents || 0 },
                            { label: 'Last 24 Hours', value: systemStats?.incidents.incidentsLast24Hours || 0 },
                            { label: 'Last 7 Days', value: systemStats?.incidents.incidentsLast7Days || 0 },
                            { label: 'Last 30 Days', value: systemStats?.incidents.incidentsLast30Days || 0 },
                        ]}
                    />
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <StatCard
                        title="Claims"
                        icon={<FileTextOutlined />}
                        iconColor="#eab308"
                        isLoading={isLoading}
                        stats={[
                            { label: 'Total', value: systemStats?.claims.totalClaims || 0 },
                            { label: 'Last 24 Hours', value: systemStats?.claims.claimsLast24Hours || 0 },
                            { label: 'Last 7 Days', value: systemStats?.claims.claimsLast7Days || 0 },
                            { label: 'Last 30 Days', value: systemStats?.claims.claimsLast30Days || 0 },
                        ]}
                    />
                </Col>
            </Row>
        </div>
    );
}

// ============================================================================
// Infrastructure Section
// ============================================================================

function InfrastructureSection({
    systemStats,
    isLoading,
}: {
    systemStats: SystemStats | undefined;
    isLoading: boolean;
}) {
    const { hasAccess } = useAdminModulePermissions('system');

    if (!hasAccess) return null;

    return (
        <div className="mb-8">
            <SectionHeader icon={Server} title="Infrastructure" color="#06b6d4" />
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={8}>
                    <StatCard
                        title="File Storage"
                        icon={<CloudUploadOutlined />}
                        iconColor="#3b82f6"
                        isLoading={isLoading}
                        stats={[
                            { label: 'Total Used', value: formatFileSize(systemStats?.files.totalFileSize || 0) },
                            {
                                label: 'Last 24 Hours',
                                value: formatFileSize(systemStats?.files.fileSizeLast24Hours || 0),
                            },
                            { label: 'Last 7 Days', value: formatFileSize(systemStats?.files.fileSizeLast7Days || 0) },
                            {
                                label: 'Last 30 Days',
                                value: formatFileSize(systemStats?.files.fileSizeLast30Days || 0),
                            },
                        ]}
                    />
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <StatCard
                        title="Emails Sent"
                        icon={<MailOutlined />}
                        iconColor="#eab308"
                        isLoading={isLoading}
                        stats={[
                            { label: 'Total', value: systemStats?.emails.totalEmailsSent || 0 },
                            { label: 'Last 24 Hours', value: systemStats?.emails.emailsSentLast24Hours || 0 },
                            { label: 'Last 7 Days', value: systemStats?.emails.emailsSentLast7Days || 0 },
                            { label: 'Last 30 Days', value: systemStats?.emails.emailsSentLast30Days || 0 },
                        ]}
                    />
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <StatCard
                        title="SMS Sent"
                        icon={<MessageOutlined />}
                        iconColor="#6366f1"
                        isLoading={isLoading}
                        stats={[
                            { label: 'Total', value: systemStats?.sms.totalSmsSent || 0 },
                            { label: 'Last 24 Hours', value: systemStats?.sms.smsSentLast24Hours || 0 },
                            { label: 'Last 7 Days', value: systemStats?.sms.smsSentLast7Days || 0 },
                            { label: 'Last 30 Days', value: systemStats?.sms.smsSentLast30Days || 0 },
                        ]}
                    />
                </Col>
            </Row>
        </div>
    );
}

// ============================================================================
// AI Section
// ============================================================================

function AiSection({ systemStats, isLoading }: { systemStats: SystemStats | undefined; isLoading: boolean }) {
    const { hasAccess } = useAdminModulePermissions('ai');

    if (!hasAccess) return null;

    return (
        <div className="mb-8">
            <SectionHeader icon={Cpu} title="AI & Machine Learning" color="#ec4899" />
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={8}>
                    <StatCard
                        title="AI Token Usage"
                        icon={<RobotOutlined />}
                        iconColor="#06b6d4"
                        isLoading={isLoading}
                        stats={[
                            { label: 'Total Tokens', value: (systemStats?.ai.totalTokensUsed || 0).toLocaleString() },
                            {
                                label: 'Last 24 Hours',
                                value: (systemStats?.ai.tokensUsedLast24Hours || 0).toLocaleString(),
                            },
                            {
                                label: 'Last 7 Days',
                                value: (systemStats?.ai.tokensUsedLast7Days || 0).toLocaleString(),
                            },
                            {
                                label: 'Last 30 Days',
                                value: (systemStats?.ai.tokensUsedLast30Days || 0).toLocaleString(),
                            },
                        ]}
                    />
                </Col>
            </Row>
        </div>
    );
}

// ============================================================================
// Empty State
// ============================================================================

function EmptyState() {
    return (
        <div className="flex min-h-[400px] items-center justify-center">
            <Empty
                image={<BarChart3 size={64} className="mx-auto text-quaternary-foreground" />}
                description={
                    <div className="text-center">
                        <Typography.Text strong className="block text-lg">
                            No Statistics Available
                        </Typography.Text>
                        <Typography.Text type="secondary">
                            You don't have permission to view any platform statistics.
                        </Typography.Text>
                    </div>
                }
            />
        </div>
    );
}

// ============================================================================
// Main Component
// ============================================================================

function StatisticsPage() {
    // Check permissions for each module
    const { hasAccess: hasUsersAccess } = useAdminModulePermissions('users');
    const { hasAccess: hasOrgsAccess } = useAdminModulePermissions('orgs');
    const { hasAccess: hasSystemAccess } = useAdminModulePermissions('system');
    const { hasAccess: hasAiAccess } = useAdminModulePermissions('ai');

    // Determine if user can see anything
    const hasAnyAccess = hasUsersAccess || hasOrgsAccess || hasSystemAccess || hasAiAccess;

    // Fetch data conditionally based on permissions
    const {
        data: systemStats,
        isLoading,
        error,
    } = trpc.admin.stats.getSystemStats.useQuery(undefined, {
        enabled: hasAnyAccess,
    });

    return (
        <PageController route={Route}>
            {!hasAnyAccess ? (
                <EmptyState />
            ) : (
                <TRPCErrorBoundary error={error}>
                    <EngagementAlertBanner />

                    <SummarySection
                        systemStats={systemStats}
                        isLoading={isLoading}
                        hasUsersAccess={hasUsersAccess}
                        hasOrgsAccess={hasOrgsAccess}
                    />

                    <Divider className="my-6" />

                    <UsersOrgsSection systemStats={systemStats} isLoading={isLoading} />
                    <BusinessMetricsSection systemStats={systemStats} isLoading={isLoading} />
                    <InfrastructureSection systemStats={systemStats} isLoading={isLoading} />
                    <AiSection systemStats={systemStats} isLoading={isLoading} />
                </TRPCErrorBoundary>
            )}
        </PageController>
    );
}
